import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Badge,
  Chip,
  Typography,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ChipProps,
  Stack,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  People as PeopleIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Clear as ClearIcon,
  FilterList as FilterListIcon,
} from '@mui/icons-material';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  SortingState,
  PaginationState,
} from '@tanstack/react-table';
import { useQuery } from '@tanstack/react-query';
import { User, Company, Role } from '../../types';
import { apiClient } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import HelpTooltip from '../common/HelpTooltip';
import DataTableHeaderCell from '../common/DataTableHeaderCell';
import { useMobileTopBarActions } from '../common/MobileTopBarActionsContext';
import PageHeader from '../common/PageHeader';
import PageHeaderActionButton from '../common/PageHeaderActionButton';
import { useIsMobileShell } from '../common/useIsMobileShell';
import PaginationFooter from '../common/PaginationFooter';
import ListSurface from '../common/ListSurface';
import { errorMessage } from '../../utils/errorMessage';
import { getColumnKey } from '../../utils/table';
import { tableSortFromSorting } from '../../utils/tableSort';
import ColumnVisibilityMenu from '../common/ColumnVisibilityMenu';
import NotebookColumnTab from '../common/NotebookColumnTab';
import { createUserColumns } from './userColumns';
import UserActionsMenu from './UserActionsMenu';
import IdentityAvatar from '../common/IdentityAvatar';
import { getPersonInitials } from '../../utils/initials';

interface UserListProps {
  onCreateUser: () => void;
  onEditUser: (user: User) => void;
  onDeleteUser: (user: User) => void;
}

interface SearchParams {
  page: number;
  size: number;
  sort?: string;
  direction?: 'asc' | 'desc';
  companyId?: string;
  name?: string;
}

export const matchesUserSearch = (user: User, searchTerm: string): boolean => {
  const normalizedSearch = searchTerm.trim().toLowerCase();
  if (!normalizedSearch) return true;

  return [user.username, user.firstName, user.lastName]
    .some(value => value?.toLowerCase().includes(normalizedSearch));
};

const UserList: React.FC<UserListProps> = ({
  onCreateUser,
  onEditUser,
  onDeleteUser,
}) => {
  const { user: currentUser } = useAuth();
  const { t, language } = useLanguage();
  const isMobile = useIsMobileShell();
  const mobileTopBarActions = useMobileTopBarActions();
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<string>('');
  const [searchParams, setSearchParams] = useState<SearchParams>({
    page: 0,
    size: 10,
    sort: 'fullName',
    direction: 'asc',
  });

  const isSuperAdmin = currentUser?.role === Role.SUPER_ADMIN;
  const canWrite = isSuperAdmin || currentUser?.role === Role.COMPANY_ADMIN;

  // Column visibility state
  const [columnVisibility, setColumnVisibility] = useState({
    fullName: true,
    email: true,
    role: true,
    company: true,
    createdAt: true,
    actions: true,
  });
  
  // Column menu state
  const [columnMenuAnchor, setColumnMenuAnchor] = useState<null | HTMLElement>(null);

  // Column visibility handlers
  const handleColumnMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setColumnMenuAnchor(event.currentTarget);
  };

  const handleColumnMenuClose = () => {
    setColumnMenuAnchor(null);
  };

  const toggleColumnVisibility = (columnKey: keyof typeof columnVisibility) => {
    setColumnVisibility(prev => ({
      ...prev,
      [columnKey]: !prev[columnKey]
    }));
  };

  // Load companies for super admin filtering
  useEffect(() => {
    const loadCompanies = async () => {
      if (isSuperAdmin) {
        try {
          const companiesData = await apiClient.getCompanies();
          setCompanies(companiesData);
        } catch (error: unknown) {
          setError(errorMessage(error, 'Failed to load companies'));
        }
      }
    };
    loadCompanies();
  }, [isSuperAdmin]);

  const usersQuery = useQuery({
    queryKey: ['users', isSuperAdmin ? 'all' : currentUser?.companyId],
    queryFn: () => isSuperAdmin
      ? apiClient.getUsers()
      : apiClient.getUsersByCompany(currentUser?.companyId || ''),
    enabled: Boolean(currentUser) && (isSuperAdmin || Boolean(currentUser?.companyId)),
    retry: false,
    staleTime: 30000,
  });

  const filteredUsers = useMemo(() => {
    const filtered = (usersQuery.data || []).filter((user) => {
      const matchesSearch = matchesUserSearch(user, searchTerm);
      const matchesCompany = !isSuperAdmin || !selectedCompany || user.companyId === selectedCompany;
      return matchesSearch && matchesCompany;
    });

    return filtered.sort((a, b) => {
      const valueFor = (user: User) => {
        switch (searchParams.sort) {
          case 'fullName': return `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.username;
          case 'createdAt': return user.createdAt || '';
          case 'username': return user.username || '';
          case 'email': return user.email || '';
          case 'role': return user.role || '';
          default: return `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.username;
        }
      };
      const result = valueFor(a).localeCompare(valueFor(b));
      return searchParams.direction === 'desc' ? -result : result;
    });
  }, [usersQuery.data, isSuperAdmin, selectedCompany, searchTerm, searchParams.sort, searchParams.direction]);

  const totalUsers = filteredUsers.length;
  const totalPages = Math.ceil(totalUsers / (searchParams.size || 10));
  const users = useMemo(() => {
    const startIndex = (searchParams.page || 0) * (searchParams.size || 10);
    return filteredUsers.slice(startIndex, startIndex + (searchParams.size || 10));
  }, [filteredUsers, searchParams.page, searchParams.size]);
  const loading = usersQuery.isLoading;
  const displayError = usersQuery.error
    ? errorMessage(usersQuery.error, 'Failed to load users')
    : error;


  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPagination(prev => ({ ...prev, pageIndex: 0 }));
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setPagination(prev => ({ ...prev, pageIndex: 0 }));
  };

  const getRoleColor = (role: Role): ChipProps['color'] => {
    switch (role) {
      case Role.SUPER_ADMIN:
        return 'error';
      case Role.COMPANY_ADMIN:
        return 'warning';
      case Role.EMPLOYEE:
        return 'info';
      default:
        return 'default';
    }
  };

  const renderRoleBadge = (user: User) => (
    <Chip size="small" color={getRoleColor(user.role)} variant="outlined" label={t(`role.${user.role}`)} sx={{ height: 24, flexShrink: 0, fontWeight: 600 }} />
  );

  const canEditUser = useCallback((user: User) => {
    if (isSuperAdmin) return true;
    if (currentUser?.role === Role.COMPANY_ADMIN) {
      // Company admin can edit employees in their company, but not other admins
      return user.companyId === currentUser.companyId && user.role === Role.EMPLOYEE;
    }
    return false;
  }, [currentUser?.companyId, currentUser?.role, isSuperAdmin]);

  const getUsersHelpContent = () => {
    if (language === 'tr') {
      return (
        <Box>
          <Typography variant="body2" paragraph>
            <strong>Bu sayfa sistem kullanıcılarını ve rollerini yönetmenizi sağlar.</strong>
          </Typography>
          
          <Typography variant="subtitle2" gutterBottom>
            🔍 <strong>Arama ve Filtreleme:</strong>
          </Typography>
          <Typography variant="body2" paragraph>
            • Arama kutusuna kullanıcı adı, ad veya soyad yazarak arama yapın<br/>
            {isSuperAdmin && <>• Şirket filtresini kullanarak belirli şirketteki kullanıcıları görün<br/></>}
            • Tabloda gösterilen sütunları özelleştirmek için Sütunlar düğmesini kullanın
          </Typography>

          <Typography variant="subtitle2" gutterBottom>
            ➕ <strong>Kullanıcı Ekleme:</strong>
          </Typography>
          <Typography variant="body2" paragraph>
            • "Kullanıcı Ekle" butonuna tıklayarak yeni kullanıcı ekleyin<br/>
            • Ad, soyad, kullanıcı adı ve şifre girin; e-posta isteğe bağlıdır<br/>
            {isSuperAdmin
              ? '• Kullanıcı için rol ve şirket seçin'
              : '• Bu şirket için Çalışan hesabı oluşturabilirsiniz'}
          </Typography>

          <Typography variant="subtitle2" gutterBottom>
            👥 <strong>Kullanıcılar:</strong>
          </Typography>
          <Typography variant="body2" paragraph>
            • Kullanıcı bilgilerini güncellemek için satırdaki işlemler (⋮) menüsünü açıp Düzenle’yi seçin<br/>
            • Kullanıcıyı silmek için aynı menüyü açıp Sil’i seçin<br/>
            • Rol değişiklikleri yetki seviyenize göre sınırlıdır<br/>
            • Kendi hesabınızı bu sayfadan silemezsiniz
          </Typography>

          <Typography variant="subtitle2" gutterBottom>
            🔐 <strong>Rol Yönetimi:</strong>
          </Typography>
          <Typography variant="body2" paragraph>
            • Süper Yönetici şirketleri ve kullanıcıları yönetir; diğer kayıtlara şirket seçilebilen ekranlardan erişir<br/>
            • Şirket Yöneticisi kendi şirketindeki kullanıcıları, müşterileri, ürünleri, satışları, borçları ve giderleri yönetir<br/>
            • Çalışan müşteri ve ürünleri görüntüler, satış ve borç/ödeme işlemleri yapar<br/>
            • Rol renkleri ile görsel ayrım yapılır
          </Typography>

          <Typography variant="subtitle2" gutterBottom>
            💡 <strong>İpuçları:</strong>
          </Typography>
          <Typography variant="body2">
            • Email adresi zorunlu değildir ancak iletişim için önerilir<br/>
            • Kullanıcı adı benzersiz olmalıdır<br/>
            • Kullanıcı silmeden önce tüm işlemlerinin tamamlandığından emin olun
          </Typography>
        </Box>
      );
    } else {
      return (
        <Box>
          <Typography variant="body2" paragraph>
            <strong>This page allows you to manage system users and their roles.</strong>
          </Typography>
          
          <Typography variant="subtitle2" gutterBottom>
            🔍 <strong>Search and Filtering:</strong>
          </Typography>
          <Typography variant="body2" paragraph>
            • Use the search box to find users by username, first name, or last name<br/>
            {isSuperAdmin && <>• Use the company filter to view users from specific companies<br/></>}
            • Use the Columns button to customize which columns appear in the table
          </Typography>

          <Typography variant="subtitle2" gutterBottom>
            ➕ <strong>Adding Users:</strong>
          </Typography>
          <Typography variant="body2" paragraph>
            • Click "Add User" button to create new users<br/>
            • Enter first name, last name, username, and a password; email is optional<br/>
            {isSuperAdmin
              ? '• Choose the user’s role and company'
              : '• You can create Employee accounts for your company'}
          </Typography>

          <Typography variant="subtitle2" gutterBottom>
            👥 <strong>Users:</strong>
          </Typography>
          <Typography variant="body2" paragraph>
            • Open the row actions (⋮) menu and choose Edit to update user information<br/>
            • Open the same menu and choose Delete to remove a user<br/>
            • Role changes are limited based on your permission level<br/>
            • You cannot delete your own account from this page
          </Typography>

          <Typography variant="subtitle2" gutterBottom>
            🔐 <strong>Role Management:</strong>
          </Typography>
          <Typography variant="body2" paragraph>
            • Super Admins manage companies and users and access other records on screens with company selection<br/>
            • Company Admins manage users, customers, items, sales, debts, and expenses for their company<br/>
            • Employees view customers and items and can record sales, debts, and payments<br/>
            • Role colors provide visual distinction
          </Typography>

          <Typography variant="subtitle2" gutterBottom>
            💡 <strong>Tips:</strong>
          </Typography>
          <Typography variant="body2">
            • Email address is not required but recommended for communication<br/>
            • Username must be unique across the system<br/>
            • Ensure all user operations are completed before deletion
          </Typography>
        </Box>
      );
    }
  };

  const allColumns = useMemo(() => createUserColumns({
    t, language, canWrite, getRoleColor, canEditUser,
    currentUserId: currentUser?.id, onEdit: onEditUser, onDelete: onDeleteUser,
  }), [t, language, canWrite, canEditUser, onEditUser, onDeleteUser, currentUser?.id]);

  // Filter columns based on visibility
  const columns = useMemo(() => {
    return allColumns.filter((column) => {
      const key = getColumnKey(column);
      if (key === 'company' && !isSuperAdmin) return false;
      if (key === 'actions' && !canWrite) return false;
      return columnVisibility[key as keyof typeof columnVisibility] !== false;
    });
  }, [allColumns, columnVisibility, isSuperAdmin, canWrite]);

  // TanStack React Table configuration
  const [sorting, setSorting] = useState<SortingState>([{ id: 'fullName', desc: false }]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: searchParams.page || 0,
    pageSize: searchParams.size || 10,
  });

  const table = useReactTable({
    data: users,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualSorting: true,
    pageCount: totalPages,
    state: {
      sorting,
      pagination,
    },
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    sortDescFirst: false,
  });

  // Handle pagination change
  useEffect(() => {
    if (pagination.pageIndex !== searchParams.page || pagination.pageSize !== searchParams.size) {
      setSearchParams(prev => ({
        ...prev,
        page: pagination.pageIndex,
        size: pagination.pageSize,
      }));
    }
  }, [pagination.pageIndex, pagination.pageSize, searchParams.page, searchParams.size]);

  // Handle sorting change
  useEffect(() => {
    const sort = tableSortFromSorting(sorting);
    setPagination(prev => ({ ...prev, pageIndex: 0 }));
    setSearchParams(prev => ({
      ...prev,
      ...sort,
      page: 0,
    }));
  }, [sorting]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', pb: isMobile ? 'calc(61px + env(safe-area-inset-bottom))' : 0 }}>
      <PageHeader
        title={t('nav.users')}
        icon={<PeopleIcon />}
        accent="users"
        titleAccessory={<HelpTooltip helpId="users-page-help" title={t('help.users.title')} content={getUsersHelpContent()} maxWidth={700} placement="bottom-start" />}
        description={t('users.subtitle')}
        mobileMode="hidden"
        actions={canWrite ? (
          <PageHeaderActionButton startIcon={<AddIcon />} onClick={onCreateUser}>
            {isSuperAdmin ? t('users.addUser') : t('users.addEmployee')}
          </PageHeaderActionButton>
        ) : undefined}
      />

      {isMobile && !mobileTopBarActions && <HelpTooltip helpId="users-page-help" title={t('help.users.title')} content={getUsersHelpContent()} maxWidth={700} placement="bottom-start" />}

      <ListSurface sx={{ mb: { xs: 0, md: 2.5 }, overflow: { xs: 'hidden', md: 'visible' } }}>
        <ListSurface.Toolbar sx={{ p: { xs: '12px 16px 0', md: 2.5 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, md: 1.25 } }}>
            <TextField
              fullWidth
              size={isMobile ? 'small' : 'medium'}
              placeholder={t('users.searchUsers')}
              aria-label={t('users.searchUsers')}
              value={searchTerm}
              onChange={handleSearchChange}
              sx={isMobile ? {
                flex: 1,
                minWidth: 0,
                '& .MuiOutlinedInput-root': {
                  height: 44,
                  borderRadius: 2,
                  bgcolor: 'action.hover',
                  '& fieldset, &:hover fieldset': { border: 0 },
                  '&.Mui-focused fieldset': { border: '2px solid', borderColor: 'primary.main' },
                },
              } : undefined}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
                endAdornment: searchTerm ? <InputAdornment position="end"><IconButton aria-label={t('common.clearFilters')} onClick={handleClearSearch} size="small"><ClearIcon /></IconButton></InputAdornment> : undefined,
              }}
            />
            {isMobile && isSuperAdmin && <Badge badgeContent={selectedCompany ? 1 : 0} color="warning" invisible={!selectedCompany} sx={{ '& .MuiBadge-badge': { minWidth: 17, height: 17, fontSize: 10, fontWeight: 700 } }}>
              <IconButton aria-label={t('filters.company')} aria-expanded={showFilters} onClick={() => setShowFilters(value => !value)} sx={{ width: 44, height: 44, flexShrink: 0 }}><FilterListIcon /></IconButton>
            </Badge>}
            {isMobile && canWrite && <IconButton aria-label={isSuperAdmin ? t('users.addUser') : t('users.addEmployee')} onClick={onCreateUser} sx={{ width: 44, height: 44, flexShrink: 0, color: 'primary.main' }}><AddIcon sx={{ fontSize: 30 }} /></IconButton>}
            {!isMobile && isSuperAdmin && <Badge badgeContent={selectedCompany ? 1 : 0} color="warning" invisible={!selectedCompany}>
              <Button
                variant={showFilters ? 'contained' : 'outlined'}
                startIcon={<FilterListIcon />}
                endIcon={showFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                aria-expanded={showFilters}
                aria-controls="user-company-filters"
                onClick={() => setShowFilters(value => !value)}
                sx={{ minHeight: 44, whiteSpace: 'nowrap' }}
              >
                {t('common.filters')}
              </Button>
            </Badge>}
          </Box>
          {/* Filter Panel */}
          {isSuperAdmin && (
            <Collapse in={showFilters} id="user-company-filters">
              <Box sx={{
                mt: { xs: 1, sm: 2 },
                p: { xs: 1.5, sm: 2 },
                bgcolor: 'action.hover',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1.5,
              }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Box sx={{ minWidth: 200, flex: 1 }}>
                    <FormControl fullWidth>
                      <InputLabel>{t('filters.company')}</InputLabel>
                      <Select
                        value={selectedCompany}
                        label={t('filters.company')}
                        onChange={(e) => { setSelectedCompany(e.target.value); setPagination(prev => ({ ...prev, pageIndex: 0 })); }}
                      >
                        <MenuItem value="">
                          <em>{t('filters.allCompanies')}</em>
                        </MenuItem>
                        {companies.map((company) => (
                          <MenuItem key={company.id} value={company.id}>
                            {company.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                  {selectedCompany && (
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<ClearIcon />}
                      onClick={() => { setSelectedCompany(''); setPagination(prev => ({ ...prev, pageIndex: 0 })); }}
                    >
                      {t('filters.clear')}
                    </Button>
                  )}
                </Box>
              </Box>
            </Collapse>
          )}
          {isSuperAdmin && selectedCompany && <Stack direction="row" sx={{ pt: 1 }}>
            <Chip
              size="small"
              label={`${t('filters.company')}: ${companies.find(company => company.id === selectedCompany)?.name || selectedCompany}`}
              onDelete={() => { setSelectedCompany(''); setPagination(prev => ({ ...prev, pageIndex: 0 })); }}
            />
          </Stack>}
        </ListSurface.Toolbar>

      {displayError && (
        <Alert severity="error" sx={{ mb: { xs: 1.5, md: 3 }, mx: { xs: 2, md: 0 } }} onClose={() => setError(null)}>
          {displayError}
        </Alert>
      )}

      <ListSurface.Content sx={{ position: 'relative' }}>
        <NotebookColumnTab label={t('common.columns')} accent="users" open={Boolean(columnMenuAnchor)} onClick={handleColumnMenuOpen} />
        {loading && isMobile && <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}><CircularProgress /></Box>}
        {!loading && users.length === 0 && <Box sx={{ textAlign: 'center', px: 2, py: { xs: 5, md: 8 } }}>
          <PeopleIcon sx={{ display: { xs: 'none', md: 'inline-block' }, fontSize: 56, color: 'text.disabled', mb: 1 }} />
          <Typography variant={isMobile ? 'body2' : 'h6'} color="text.secondary" sx={{ mb: 1 }}>
            {searchTerm || selectedCompany ? t('users.noSearchResults') : t('users.noUsersYet')}
          </Typography>
          {!searchTerm && !selectedCompany && canWrite && <Button variant="contained" startIcon={<AddIcon />} onClick={onCreateUser} sx={{ mt: 1 }}>
            {isSuperAdmin ? t('users.addFirstUser') : t('users.addFirstEmployee')}
          </Button>}
        </Box>}
        {(users.length > 0 || (loading && !isMobile)) && <>
        <Box component="ul" sx={{ display: { xs: 'block', md: 'none' }, listStyle: 'none', m: 0, mt: 1.5, p: 0 }}>
          {users.map(user => (
            <Box component="li" key={user.id} sx={{ borderTop: '1px solid', borderColor: 'divider', '&:first-of-type': { borderTop: 0 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 92, pl: 2, pr: 1, gap: 1.25 }}>
                <IdentityAvatar>{getPersonInitials(user.firstName, user.lastName, language, user.username) || '?'}</IdentityAvatar>
                <Box sx={{ flex: 1, minWidth: 0, py: 1.25 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                    <Typography component="h3" fontWeight={600} noWrap sx={{ minWidth: 0, fontSize: 16, lineHeight: 1.3 }}>{`${user.firstName} ${user.lastName}`}</Typography>
                    {canEditUser(user) && <Box sx={{ flexShrink: 0, '& .MuiIconButton-root': { width: 44, height: 44, color: 'text.secondary' } }}>
                      <UserActionsMenu user={user} canDelete={user.id !== currentUser?.id} onEdit={onEditUser} onDelete={onDeleteUser} />
                    </Box>}
                  </Box>
                  <Typography variant="body2" color="text.secondary" noWrap sx={{ mt: 0.1, fontSize: 13 }}>{user.username}</Typography>
                  {isSuperAdmin ? (
                    <>
                      <Typography variant="body2" color="text.secondary" noWrap sx={{ fontSize: 13 }}>{user.email || t('users.noEmail')}</Typography>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} sx={{ mt: 0.5 }}>
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ minWidth: 0 }}>{user.companyName || user.company?.name || '—'}</Typography>
                        {renderRoleBadge(user)}
                      </Stack>
                    </>
                  ) : (
                    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} sx={{ mt: 0.25 }}>
                      <Typography variant="body2" color="text.secondary" noWrap sx={{ minWidth: 0, fontSize: 13 }}>{user.email || t('users.noEmail')}</Typography>
                      {renderRoleBadge(user)}
                    </Stack>
                  )}
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
        <TableContainer sx={{ display: { xs: 'none', md: 'block' } }}>
          <Table>
            <TableHead>
              <TableRow>
                {table.getHeaderGroups().map((headerGroup) =>
                  headerGroup.headers.map((header) => (
                    <DataTableHeaderCell
                      key={header.id}
                      align={header.column.id === 'actions' ? 'right' : 'left'}
                      sort={header.column.getCanSort() ? {
                        active: Boolean(header.column.getIsSorted()),
                        direction: header.column.getIsSorted() === 'desc' ? 'desc' : 'asc',
                        onClick: () => header.column.toggleSorting(),
                      } : undefined}
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                    </DataTableHeaderCell>
                  ))
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={table.getVisibleLeafColumns().length} align="center" sx={{ py: 8 }}>
                    <CircularProgress aria-label={t('common.loading')} />
                  </TableCell>
                </TableRow>
              ) : table.getRowModel().rows.map((row) => (
                <TableRow key={row.id} hover sx={{ '&:last-child td': { borderBottom: 0 } }}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id} align={cell.column.id === 'actions' ? 'right' : 'left'}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        </>}
      </ListSurface.Content>

        {/* Custom Pagination */}
        <ListSurface.Footer>{totalUsers > 0 && (
          <PaginationFooter
            pageIndex={pagination.pageIndex}
            pageSize={pagination.pageSize}
            totalPages={totalPages}
            totalElements={totalUsers}
            showingLabel={t('common.showing')}
            ofLabel={t('common.of')}
            pageSizeLabel={t('common.rowsPerPage')}
            mobileCompact
            previousPageLabel={t('customers.previousPage')}
            nextPageLabel={t('customers.nextPage')}
            onPageChange={pageIndex => table.setPageIndex(pageIndex)}
            onPageSizeChange={pageSize => table.setPageSize(pageSize)}
          />
        )}</ListSurface.Footer>
      </ListSurface>

      <ColumnVisibilityMenu
        side
        anchorEl={columnMenuAnchor}
        onClose={handleColumnMenuClose}
        onToggle={(key) => toggleColumnVisibility(key as keyof typeof columnVisibility)}
        items={[
          { key: 'fullName', label: t('common.name'), visible: columnVisibility.fullName },
          { key: 'email', label: t('common.email'), visible: columnVisibility.email },
          { key: 'role', label: t('users.role'), visible: columnVisibility.role },
          ...(isSuperAdmin ? [{ key: 'company', label: t('users.company'), visible: columnVisibility.company }] : []),
          { key: 'createdAt', label: t('users.created'), visible: columnVisibility.createdAt },
          ...(canWrite ? [{ key: 'actions', label: t('common.actions'), visible: columnVisibility.actions }] : []),
        ]}
      />
    </Box>
  );
};

export default UserList;
